import { useState, useCallback, useEffect, useRef } from 'react';
import dataApi, { MetricsTimeRangeResponse } from '../../../../Api/data.api';
import {
  TimePreset,
  AggregationLevel,
  MetricRow,
  MetricStats,
  TIME_PRESETS,
  SelectedMetric,
  METRIC_COLORS,
  LiveModeSettings,
  DEFAULT_LIVE_MODE,
} from '../types';

export interface UseMetricsDataOptions {
  datasetId: number;
  initialTimePreset?: TimePreset;
  initialAggregation?: AggregationLevel;
  autoLoad?: boolean;
}

export interface UseMetricsDataReturn {
  // Data
  data: MetricRow[];
  dataExtent: { from: Date; to: Date } | null; // Time extent of original data (doesn't change on zoom)
  stats: Record<string, MetricStats>;
  metricKeys: string[];
  totalCount: number;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;

  // Time range
  timePreset: TimePreset;
  setTimePreset: (preset: TimePreset) => void;
  customFrom: Date | null;
  customTo: Date | null;
  setCustomRange: (from: Date | null, to: Date | null) => void;
  actualTimeRange: { from: Date | null; to: Date | null };

  // Aggregation
  aggregation: AggregationLevel;
  setAggregation: (level: AggregationLevel) => void;
  effectiveAggregation: AggregationLevel | null; // The actual aggregation used (after auto-calc)
  isZoomed: boolean; // Whether we're viewing a zoomed/selected range

  // Metric selection
  selectedMetrics: SelectedMetric[];
  toggleMetric: (key: string) => void;
  setMetricVisibility: (key: string, visible: boolean) => void;
  selectAllMetrics: () => void;
  deselectAllMetrics: () => void;
  setSelectedMetricKeys: (keys: string[]) => void;

  // Live mode
  liveMode: LiveModeSettings;
  setLiveMode: (settings: Partial<LiveModeSettings>) => void;

  // Actions
  refresh: () => Promise<void>;
  loadMore: (take?: number) => Promise<void>;
  fetchForZoomRange: (from: Date, to: Date) => Promise<void>;
  resetToPreset: () => Promise<void>;
}

export function useMetricsData(options: UseMetricsDataOptions): UseMetricsDataReturn {
  const {
    datasetId,
    initialTimePreset = '24h',
    initialAggregation = 'raw',
    autoLoad = true,
  } = options;

  // Data state
  const [data, setData] = useState<MetricRow[]>([]);
  const [overviewData, setOverviewData] = useState<MetricRow[]>([]); // Original data from preset, used as base for merging zoom data
  const [dataExtent, setDataExtent] = useState<{ from: Date; to: Date } | null>(null); // Time extent of original data (doesn't change on zoom)
  const [stats, setStats] = useState<Record<string, MetricStats>>({});
  const [metricKeys, setMetricKeys] = useState<string[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Time range state
  const [timePreset, setTimePreset] = useState<TimePreset>(initialTimePreset);
  const [customFrom, setCustomFrom] = useState<Date | null>(null);
  const [customTo, setCustomTo] = useState<Date | null>(null);
  const [actualTimeRange, setActualTimeRange] = useState<{ from: Date | null; to: Date | null }>({
    from: null,
    to: null,
  });

  // Aggregation state
  const [aggregation, setAggregation] = useState<AggregationLevel>(initialAggregation);
  const [effectiveAggregation, setEffectiveAggregation] = useState<AggregationLevel | null>(null);

  // Track if we're in a "zoomed" state (fetched for a specific chart selection)
  const [isZoomed, setIsZoomed] = useState(false);

  // Metric selection state
  const [selectedMetrics, setSelectedMetrics] = useState<SelectedMetric[]>([]);

  // Live mode state
  const [liveMode, setLiveModeState] = useState<LiveModeSettings>(DEFAULT_LIVE_MODE);
  const liveIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Target number of data points for optimal visualization
  const TARGET_POINTS = 5000;

  // Calculate optimal aggregation based on time range
  // Assumes data is sampled every 1 second
  const calculateOptimalAggregation = useCallback((fromDate: Date | null, toDate: Date | null): AggregationLevel => {
    if (!fromDate || !toDate) {
      return 'raw'; // No time range, use raw
    }

    const rangeMs = toDate.getTime() - fromDate.getTime();
    const rangeSeconds = rangeMs / 1000;

    // If raw data would be under target, use raw
    if (rangeSeconds <= TARGET_POINTS) {
      return 'raw';
    }

    // Calculate ideal bucket size in seconds
    const idealBucketSeconds = rangeSeconds / TARGET_POINTS;

    // Available bucket sizes in seconds
    const buckets: { level: AggregationLevel; seconds: number }[] = [
      { level: '1m', seconds: 60 },
      { level: '5m', seconds: 300 },
      { level: '15m', seconds: 900 },
      { level: '1h', seconds: 3600 },
      { level: '6h', seconds: 21600 },
      { level: '1d', seconds: 86400 },
    ];

    // Find the smallest bucket that keeps us under TARGET_POINTS
    for (const bucket of buckets) {
      const expectedPoints = rangeSeconds / bucket.seconds;
      if (expectedPoints <= TARGET_POINTS) {
        return bucket.level;
      }
    }

    // If even 1d buckets exceed target, use 1d (largest available)
    return '1d';
  }, []);

  // Calculate time range from preset
  const getTimeRange = useCallback((): { from: string | undefined; to: string | undefined } => {
    const now = new Date();

    if (timePreset === 'custom') {
      return {
        from: customFrom?.toISOString(),
        to: customTo?.toISOString(),
      };
    }

    if (timePreset === 'all') {
      return { from: undefined, to: undefined };
    }

    const presetConfig = TIME_PRESETS[timePreset];
    if (presetConfig.ms) {
      const from = new Date(now.getTime() - presetConfig.ms);
      return {
        from: from.toISOString(),
        to: now.toISOString(),
      };
    }

    return { from: undefined, to: undefined };
  }, [timePreset, customFrom, customTo]);

  // Fetch data from API
  const fetchData = useCallback(async (take?: number) => {
    setLoading(true);
    setError(null);

    try {
      const { from, to } = getTimeRange();

      // Calculate optimal aggregation if set to 'auto' (raw), or use manual selection
      let computedAggregation: AggregationLevel | undefined;
      if (aggregation === 'raw') {
        // Auto-calculate based on time range
        const fromDate = from ? new Date(from) : null;
        const toDate = to ? new Date(to) : null;
        const autoAgg = calculateOptimalAggregation(fromDate, toDate);
        computedAggregation = autoAgg === 'raw' ? undefined : autoAgg;
      } else {
        computedAggregation = aggregation;
      }

      // Track the effective aggregation used
      setEffectiveAggregation(computedAggregation || 'raw');

      const response: MetricsTimeRangeResponse = await dataApi.getDatasetMetricsTimeRange(
        datasetId,
        {
          from,
          to,
          take: take || TARGET_POINTS,
          aggregation: computedAggregation,
        }
      );

      setData(response.data);
      setOverviewData(response.data); // Update overview data on preset fetches (not on zoom)
      setStats(response.stats);
      setMetricKeys(response.metricKeys);
      setTotalCount(response.totalCount);
      setLastUpdated(new Date());
      setIsZoomed(false); // Reset zoom state when fetching by preset

      // Update actual time range
      setActualTimeRange({
        from: response.timeRange.actualFrom ? new Date(response.timeRange.actualFrom) : null,
        to: response.timeRange.actualTo ? new Date(response.timeRange.actualTo) : null,
      });

      // Compute and store the data extent (for rangeslider - doesn't change on zoom)
      if (response.data.length > 0) {
        const timestamps = response.data.map(row => new Date(row.createdAt).getTime());
        const minTime = Math.min(...timestamps);
        const maxTime = Math.max(...timestamps);
        setDataExtent({ from: new Date(minTime), to: new Date(maxTime) });
      } else {
        setDataExtent(null);
      }

      // Initialize selected metrics if not already set
      if (selectedMetrics.length === 0 && response.metricKeys.length > 0) {
        const initialSelected: SelectedMetric[] = response.metricKeys.map((key, index) => ({
          key,
          label: key.replace(/_/g, ' '),
          color: METRIC_COLORS[index % METRIC_COLORS.length],
          visible: true,
        }));
        setSelectedMetrics(initialSelected);
      } else {
        // Update colors for any new metrics
        const existingKeys = new Set(selectedMetrics.map(m => m.key));
        const newMetrics = response.metricKeys.filter(k => !existingKeys.has(k));

        if (newMetrics.length > 0) {
          const newSelected: SelectedMetric[] = newMetrics.map((key, index) => ({
            key,
            label: key.replace(/_/g, ' '),
            color: METRIC_COLORS[(selectedMetrics.length + index) % METRIC_COLORS.length],
            visible: true,
          }));
          setSelectedMetrics(prev => [...prev, ...newSelected]);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load metrics');
      console.error('Failed to load metrics:', err);
    } finally {
      setLoading(false);
    }
  }, [datasetId, getTimeRange, aggregation, selectedMetrics.length, calculateOptimalAggregation]);

  // Refresh data
  const refresh = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  // Load more data
  const loadMore = useCallback(async (take: number = 50000) => {
    await fetchData(take);
  }, [fetchData]);

  // Fetch data for a specific zoom/selection range with auto-calculated aggregation
  // Merges new high-resolution data into existing data, replacing the zoomed time range
  const fetchForZoomRange = useCallback(async (from: Date, to: Date) => {
    setLoading(true);
    setError(null);

    try {
      // Calculate optimal aggregation for the zoomed range
      const autoAgg = calculateOptimalAggregation(from, to);
      const computedAggregation = autoAgg === 'raw' ? undefined : autoAgg;

      // Track the effective aggregation used
      setEffectiveAggregation(computedAggregation || 'raw');

      const response: MetricsTimeRangeResponse = await dataApi.getDatasetMetricsTimeRange(
        datasetId,
        {
          from: from.toISOString(),
          to: to.toISOString(),
          take: TARGET_POINTS,
          aggregation: computedAggregation,
        }
      );

      // Merge new data into existing data:
      // - Keep data BEFORE the zoom range (from overviewData)
      // - Replace data IN the zoom range with new high-res data
      // - Keep data AFTER the zoom range (from overviewData)
      const fromTime = from.getTime();
      const toTime = to.getTime();

      setData(currentData => {
        // Use overviewData as the base since it has the full extent
        const baseData = overviewData.length > 0 ? overviewData : currentData;

        // Get data outside the zoom range
        const beforeZoom = baseData.filter(row => new Date(row.createdAt).getTime() < fromTime);
        const afterZoom = baseData.filter(row => new Date(row.createdAt).getTime() > toTime);

        // Combine: before + new zoomed data + after
        const merged = [...beforeZoom, ...response.data, ...afterZoom];

        // Sort by timestamp
        return merged.sort((a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
      });

      setStats(response.stats);
      setMetricKeys(response.metricKeys);
      setTotalCount(response.totalCount);
      setLastUpdated(new Date());
      setIsZoomed(true);

      // Update actual time range
      setActualTimeRange({
        from: response.timeRange.actualFrom ? new Date(response.timeRange.actualFrom) : from,
        to: response.timeRange.actualTo ? new Date(response.timeRange.actualTo) : to,
      });

      // Update selected metrics for any new keys
      const existingKeys = new Set(selectedMetrics.map(m => m.key));
      const newMetrics = response.metricKeys.filter(k => !existingKeys.has(k));

      if (newMetrics.length > 0) {
        const newSelected: SelectedMetric[] = newMetrics.map((key, index) => ({
          key,
          label: key.replace(/_/g, ' '),
          color: METRIC_COLORS[(selectedMetrics.length + index) % METRIC_COLORS.length],
          visible: true,
        }));
        setSelectedMetrics(prev => [...prev, ...newSelected]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load metrics for selected range');
      console.error('Failed to load metrics for zoom range:', err);
    } finally {
      setLoading(false);
    }
  }, [datasetId, calculateOptimalAggregation, selectedMetrics, overviewData]);

  // Reset back to the current time preset (exit zoom mode)
  const resetToPreset = useCallback(async () => {
    setIsZoomed(false);
    await fetchData();
  }, [fetchData]);

  // Auto-load on mount and when time range or aggregation changes
  useEffect(() => {
    if (autoLoad) {
      fetchData();
    }
  }, [datasetId, timePreset, customFrom, customTo, aggregation, autoLoad]);

  // Live mode effect
  useEffect(() => {
    if (liveMode.enabled) {
      liveIntervalRef.current = setInterval(() => {
        fetchData();
      }, liveMode.intervalMs);
    } else if (liveIntervalRef.current) {
      clearInterval(liveIntervalRef.current);
      liveIntervalRef.current = null;
    }

    return () => {
      if (liveIntervalRef.current) {
        clearInterval(liveIntervalRef.current);
      }
    };
  }, [liveMode.enabled, liveMode.intervalMs, fetchData]);

  // Metric selection functions
  const toggleMetric = useCallback((key: string) => {
    setSelectedMetrics(prev =>
      prev.map(m => (m.key === key ? { ...m, visible: !m.visible } : m))
    );
  }, []);

  const setMetricVisibility = useCallback((key: string, visible: boolean) => {
    setSelectedMetrics(prev =>
      prev.map(m => (m.key === key ? { ...m, visible } : m))
    );
  }, []);

  const selectAllMetrics = useCallback(() => {
    setSelectedMetrics(prev => prev.map(m => ({ ...m, visible: true })));
  }, []);

  const deselectAllMetrics = useCallback(() => {
    setSelectedMetrics(prev => prev.map(m => ({ ...m, visible: false })));
  }, []);

  const setSelectedMetricKeys = useCallback((keys: string[]) => {
    const keySet = new Set(keys);
    setSelectedMetrics(prev =>
      prev.map(m => ({ ...m, visible: keySet.has(m.key) }))
    );
  }, []);

  // Custom range setter
  const setCustomRange = useCallback((from: Date | null, to: Date | null) => {
    setCustomFrom(from);
    setCustomTo(to);
    if (from && to) {
      setTimePreset('custom');
    }
  }, []);

  // Live mode setter
  const setLiveMode = useCallback((settings: Partial<LiveModeSettings>) => {
    setLiveModeState(prev => ({ ...prev, ...settings }));
  }, []);

  return {
    // Data
    data,
    dataExtent,
    stats,
    metricKeys,
    totalCount,
    loading,
    error,
    lastUpdated,

    // Time range
    timePreset,
    setTimePreset,
    customFrom,
    customTo,
    setCustomRange,
    actualTimeRange,

    // Aggregation
    aggregation,
    setAggregation,
    effectiveAggregation,
    isZoomed,

    // Metric selection
    selectedMetrics,
    toggleMetric,
    setMetricVisibility,
    selectAllMetrics,
    deselectAllMetrics,
    setSelectedMetricKeys,

    // Live mode
    liveMode,
    setLiveMode,

    // Actions
    refresh,
    loadMore,
    fetchForZoomRange,
    resetToPreset,
  };
}
